import * as React from 'react';
import { readCookie } from '@zapier/cookies';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';

function buildAccountSwitcherUrl(accountId) {
  return "/switch-account?account=".concat(accountId, "&next=").concat(window.location.pathname);
}

const ACCOUNT_COOKIE_NAME = 'currentAccountId';
const ACCOUNT_LOCAL_STORAGE_NAME = 'currentAccountId';

const CurrentAccountIdContext = /*#__PURE__*/React.createContext(undefined);
const useCurrentAccountId = () => React.useContext(CurrentAccountIdContext);

function getAccountIdFromCookie() {
  if (typeof document === 'undefined') {
    return undefined;
  } // check if the currentAccountId cookie has been set more than once with differing values
  // if so, we need to expire all of them and force a reauth
  // https://zapierorg.atlassian.net/browse/TA-44


  const cookies = document.cookie.split(';');
  const currentAccountIdCookies = cookies.filter(cookie => cookie.includes(ACCOUNT_COOKIE_NAME));
  const allCookiesAreTheSame = currentAccountIdCookies.every(cookie => cookie.trim() === currentAccountIdCookies[0].trim());

  if (currentAccountIdCookies.length > 1 && !allCookiesAreTheSame) {
    document.cookie = "".concat(ACCOUNT_COOKIE_NAME, "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"); // handles `zapier.com` domain

    document.cookie = "".concat(ACCOUNT_COOKIE_NAME, "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.").concat(window.location.hostname); // handles `*.zapier.com` domains

    const next = new URLSearchParams(window.location.search).get('next');
    window.location.href = addTruthyParamsToUrl(urls.LOGIN_URL, {
      next,
      reauth: true
    });
    return undefined;
  }

  return parseInt(readCookie(ACCOUNT_COOKIE_NAME), 10);
}

function setLocalStorage(accountId) {
  try {
    localStorage.setItem(ACCOUNT_LOCAL_STORAGE_NAME, accountId);
  } catch (e) {// This is only used for backwards compatibility reasons, so if we cannot store it in LocalStorage for whatever reason, we can just ignore it.
  }
}

function CurrentAccountIdProvider(props) {
  const [currentAccountId, setCurrentAccountId] = React.useState(getAccountIdFromCookie());
  /** Watch current account id cookie for changes */

  async function watchCookie() {
    const idFromCookie = getAccountIdFromCookie(); // Only take action if this is a real change

    if (idFromCookie && idFromCookie !== currentAccountId) {
      // Account id has changed
      setCurrentAccountId(idFromCookie); // Ensure that localStorage is the same as the current cookie value
      // for backwards compatibility

      setLocalStorage(idFromCookie);
    }
  }

  React.useEffect(() => {
    // Read cookie value on a set interval to watch for changes
    const timer = setInterval(watchCookie, 1000); // Ensure that localStorage is the same as the current cookie value
    // for backwards compatibility

    setLocalStorage(currentAccountId);
    /** Remove cookie watcher when we dismount */

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  });
  /** Create provider */

  return /*#__PURE__*/React.createElement(CurrentAccountIdContext.Provider, {
    value: currentAccountId
  }, props.children);
}

export { ACCOUNT_COOKIE_NAME, CurrentAccountIdProvider, buildAccountSwitcherUrl, useCurrentAccountId };
